import { useState } from "react";
import { toast } from "react-toastify";
import { ReactComponent as AdminBack } from "../../assets/images/adminBack.svg";
import { ReactComponent as Plus } from "../../assets/images/promoPlus.svg";
import { ReactComponent as Random } from "../../assets/images/promoRandom.svg";
import {
  Promocode,
  PromocodeMode,
  PromocodeType,
} from "../../interfaces/promocode";
import {
  createPromocode,
  deactivatePromocode,
  updatePromocode,
} from "../../services/axios";
import { generatePromocode, toInputDate } from "../../utils/promocode";

const categories = ["vinyl", "cassette", "cd", "merch"];

interface PromocodeFormProps {
  promocode: Promocode | null;
  setPromocode: React.Dispatch<React.SetStateAction<Promocode | null>>;
  setCreating: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
}

export const PromocodeForm = ({
  promocode,
  setPromocode,
  setCreating,
  refetch,
}: PromocodeFormProps) => {
  const [code, setCode] = useState(promocode?.code || "");
  const [type, setType] = useState<PromocodeType>(promocode?.type || "fixed");
  const [uah, setUah] = useState(promocode?.uah || 0);
  const [usd, setUsd] = useState(promocode?.usd || 0);
  const [percent, setPercent] = useState(promocode?.percent || 0);
  const [amount, setAmount] = useState(promocode?.amount || 0);
  const [mode, setMode] = useState<PromocodeMode>(promocode?.mode || "order");
  const [products, setProducts] = useState<string[]>(promocode?.products || []);
  const [productId, setProductId] = useState("");
  const [chosenCategories, setChosenCategories] = useState<string[]>(
    promocode?.categories || []
  );
  const [minPriceActive, setMinPriceActive] = useState(
    promocode?.minPriceActive || false
  );
  const [minPrice, setMinPrice] = useState(promocode?.minPrice || 0);
  const [minPriceUsd, setMinPriceUsd] = useState(promocode?.minPriceUsd || 0);
  const [withoutTerms, setWithoutTerms] = useState(
    promocode?.withoutTerms || false
  );
  const [startDate, setStartDate] = useState(toInputDate(promocode?.startDate));
  const [endDate, setEndDate] = useState(toInputDate(promocode?.endDate));

  const showError = (message: string) => {
    toast(message, {
      className: "toast__error",
      bodyClassName: "toast__body",
      progressClassName: "toast__progressBar--error",
    });
  };

  const goBack = () => {
    setPromocode(null);
    setCreating(false);
    refetch();
  };

  const addProduct = () => {
    const trimmed = productId.trim();
    if (!trimmed || products.includes(trimmed)) {
      return;
    }
    setProducts([...products, trimmed]);
    setProductId("");
  };

  const toggleCategory = (category: string) => {
    setChosenCategories(
      chosenCategories.includes(category)
        ? chosenCategories.filter((item) => item !== category)
        : [...chosenCategories, category]
    );
  };

  const submit = async () => {
    if (!code) {
      showError("Enter discount code");
      return;
    }
    if (type === "fixed" && !uah && !usd) {
      showError("Enter denomination");
      return;
    }
    if (type === "percent" && !percent) {
      showError("Enter percent");
      return;
    }
    if (mode === "products" && !products.length) {
      showError("Add at least one product");
      return;
    }
    if (mode === "categories" && !chosenCategories.length) {
      showError("Choose at least one category");
      return;
    }
    if (minPriceActive && !minPrice && !minPriceUsd) {
      showError("Enter min price");
      return;
    }
    if (!withoutTerms && (!startDate || !endDate)) {
      showError("Choose terms");
      return;
    }

    const body = {
      code: code.toUpperCase(),
      type,
      uah: type === "fixed" ? Number(uah) : 0,
      usd: type === "fixed" ? Number(usd) : 0,
      percent: type === "percent" ? Number(percent) : 0,
      amount: Number(amount),
      mode,
      products: mode === "products" ? products : [],
      categories: mode === "categories" ? chosenCategories : [],
      minPriceActive,
      minPrice: minPriceActive ? Number(minPrice) : 0,
      minPriceUsd: minPriceActive ? Number(minPriceUsd) : 0,
      withoutTerms,
      startDate: withoutTerms || !startDate ? null : startDate,
      endDate: withoutTerms || !endDate ? null : endDate,
    };

    const data = promocode
      ? await updatePromocode({ ...body, _id: promocode._id })
      : await createPromocode(body);

    if (data?.error) {
      showError("Such code already exists");
      return;
    }

    goBack();
  };

  const deactivate = async () => {
    if (!promocode) {
      return;
    }
    const data = await deactivatePromocode(promocode._id, !promocode.active);

    if (data?.error) {
      showError("An active code with this name already exists");
      return;
    }

    goBack();
  };

  return (
    <div className="admin__addGoods">
      <div className="admin__addGoods--wrapper">
        <div
          className="admin__back--link"
          style={{ cursor: "pointer" }}
          onClick={goBack}
        >
          <AdminBack />
        </div>
        <div className="admin__addGoods--title">
          {promocode ? "Editing Discount" : "Creating Discount"}
        </div>
      </div>

      <p className="admin__addGoods--label">Сode name</p>
      <div className="admin__promo--codeWrapper">
        <input
          type="text"
          className="admin__addGoods--input admin__promo--codeInput"
          placeholder="Enter Discount Code"
          maxLength={12}
          value={code}
          onChange={(event) => {
            setCode(event.target.value.toUpperCase());
          }}
        />
        <button
          type="button"
          className="admin__promo--randomBtn"
          onClick={() => {
            setCode(generatePromocode());
          }}
        >
          Random code
          <Random />
        </button>
      </div>
      <p className="admin__promo--hint">*Max 12 symbols</p>

      <p className="admin__addGoods--label">Parameters:</p>
      <div
        className="admin__addGoods--input admin__addGoods--radio"
        style={{ color: type === "fixed" ? "#ffffff" : "" }}
        onClick={() => {
          setType("fixed");
        }}
      >
        <div
          className={
            type === "fixed"
              ? "admin__addGoods--radioSquare admin__promo--radioRound admin__addGoods--radioChoosen"
              : "admin__addGoods--radioSquare admin__promo--radioRound"
          }
        ></div>
        Fixed
      </div>
      <div
        className="admin__addGoods--input admin__addGoods--radio admin__addGoods--spaceT"
        style={{ color: type === "percent" ? "#ffffff" : "" }}
        onClick={() => {
          setType("percent");
        }}
      >
        <div
          className={
            type === "percent"
              ? "admin__addGoods--radioSquare admin__promo--radioRound admin__addGoods--radioChoosen"
              : "admin__addGoods--radioSquare admin__promo--radioRound"
          }
        ></div>
        Percent
      </div>

      <p className="admin__addGoods--label">Denomination:</p>
      {type === "fixed" ? (
        <div className="admin__addGoods--inputWrapper">
          <div className="admin__addGoods--priceWrapper">
            <input
              type="number"
              className="admin__addGoods--input"
              placeholder="0"
              value={uah || ""}
              onChange={(event) => {
                setUah(Number(event.target.value));
              }}
            />
            <p className="admin__addGoods--currency">UAH</p>
          </div>
          <div className="admin__addGoods--priceWrapper">
            <input
              type="number"
              className="admin__addGoods--input"
              placeholder="0"
              value={usd || ""}
              onChange={(event) => {
                setUsd(Number(event.target.value));
              }}
            />
            <p className="admin__addGoods--currency">USD</p>
          </div>
        </div>
      ) : (
        <div className="admin__addGoods--priceWrapper">
          <input
            type="number"
            className="admin__addGoods--input"
            placeholder="0"
            value={percent || ""}
            onChange={(event) => {
              setPercent(Number(event.target.value));
            }}
          />
          <p className="admin__addGoods--currency">%</p>
        </div>
      )}

      <p className="admin__addGoods--label">Amount</p>
      <input
        type="number"
        className="admin__addGoods--input admin__promo--amountInput"
        placeholder="∞"
        value={amount || ""}
        onChange={(event) => {
          setAmount(Number(event.target.value));
        }}
      />
      <p className="admin__promo--hint">
        *Leave empty for unlimited uses
        {promocode ? `. Already used: ${promocode.used}` : ""}
      </p>

      <p className="admin__addGoods--label">Mode</p>
      <div
        className="admin__addGoods--input admin__addGoods--radio"
        style={{ color: mode === "order" ? "#ffffff" : "" }}
        onClick={() => {
          setMode("order");
        }}
      >
        <div
          className={
            mode === "order"
              ? "admin__addGoods--radioSquare admin__promo--radioRound admin__addGoods--radioChoosen"
              : "admin__addGoods--radioSquare admin__promo--radioRound"
          }
        ></div>
        To the entire order
      </div>
      <div
        className="admin__addGoods--input admin__addGoods--radio admin__addGoods--spaceT"
        style={{ color: mode === "products" ? "#ffffff" : "" }}
        onClick={() => {
          setMode("products");
        }}
      >
        <div
          className={
            mode === "products"
              ? "admin__addGoods--radioSquare admin__promo--radioRound admin__addGoods--radioChoosen"
              : "admin__addGoods--radioSquare admin__promo--radioRound"
          }
        ></div>
        Product(s)
      </div>
      {mode === "products" && (
        <>
          <div className="admin__addGoods--spaceT">
            <input
              type="text"
              className="admin__addGoods--input"
              placeholder="Enter ID"
              value={productId}
              onChange={(event) => {
                setProductId(event.target.value);
              }}
            />
          </div>
          <button
            type="button"
            className="admin__promo--addProduct"
            onClick={addProduct}
          >
            Add Product
            <Plus className="admin__promo--addProductSvg" />
          </button>
          <p className="admin__promo--hint">
            *Enter the ID of any language version — the other one is linked
            automatically
          </p>
          {products.length > 0 && (
            <ul className="admin__promo--products">
              {products.map((item) => (
                <li className="admin__promo--product" key={item}>
                  <p className="admin__promo--value">{item}</p>
                  <p
                    className="admin__promo--remove"
                    onClick={() => {
                      setProducts(products.filter((id) => id !== item));
                    }}
                  >
                    Delete
                  </p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <div
        className="admin__addGoods--input admin__addGoods--radio admin__addGoods--spaceT"
        style={{ color: mode === "categories" ? "#ffffff" : "" }}
        onClick={() => {
          setMode("categories");
        }}
      >
        <div
          className={
            mode === "categories"
              ? "admin__addGoods--radioSquare admin__promo--radioRound admin__addGoods--radioChoosen"
              : "admin__addGoods--radioSquare admin__promo--radioRound"
          }
        ></div>
        Category
      </div>
      {mode === "categories" && (
        <div className="admin__promo--categories">
          {categories.map((category) => (
            <div
              key={category}
              className="admin__addGoods--input admin__addGoods--radio"
              style={{
                color: chosenCategories.includes(category) ? "#ffffff" : "",
              }}
              onClick={() => {
                toggleCategory(category);
              }}
            >
              <div
                className={
                  chosenCategories.includes(category)
                    ? "admin__addGoods--radioSquare admin__addGoods--radioChoosen"
                    : "admin__addGoods--radioSquare"
                }
              ></div>
              {category}
            </div>
          ))}
        </div>
      )}

      <p className="admin__addGoods--label">Min. Price</p>
      <div
        className="admin__addGoods--input admin__addGoods--radio"
        style={{ color: minPriceActive ? "#ffffff" : "" }}
        onClick={() => {
          setMinPriceActive(!minPriceActive);
        }}
      >
        <div
          className={
            minPriceActive
              ? "admin__addGoods--radioSquare admin__addGoods--radioChoosen"
              : "admin__addGoods--radioSquare"
          }
        ></div>
        Activate
      </div>
      {minPriceActive && (
        <div className="admin__addGoods--inputWrapper admin__addGoods--spaceT">
          <div className="admin__addGoods--priceWrapper">
            <input
              type="number"
              className="admin__addGoods--input"
              placeholder="0"
              value={minPrice || ""}
              onChange={(event) => {
                setMinPrice(Number(event.target.value));
              }}
            />
            <p className="admin__addGoods--currency">UAH</p>
          </div>
          <div className="admin__addGoods--priceWrapper">
            <input
              type="number"
              className="admin__addGoods--input"
              placeholder="0"
              value={minPriceUsd || ""}
              onChange={(event) => {
                setMinPriceUsd(Number(event.target.value));
              }}
            />
            <p className="admin__addGoods--currency">USD</p>
          </div>
        </div>
      )}

      <p className="admin__addGoods--label">Terms</p>
      <div className="admin__addGoods--inputWrapper">
        <div className="admin__promo--dateWrapper">
          <p className="admin__promo--dateLabel">Start</p>
          <input
            type="date"
            className="admin__addGoods--input"
            disabled={withoutTerms}
            value={startDate}
            onChange={(event) => {
              setStartDate(event.target.value);
            }}
          />
        </div>
        <div className="admin__promo--dateWrapper">
          <p className="admin__promo--dateLabel">End</p>
          <input
            type="date"
            className="admin__addGoods--input"
            disabled={withoutTerms}
            value={endDate}
            onChange={(event) => {
              setEndDate(event.target.value);
            }}
          />
        </div>
      </div>
      <div
        className="admin__addGoods--input admin__addGoods--radio admin__addGoods--spaceT admin__promo--withoutTerms"
        style={{ color: withoutTerms ? "#ffffff" : "" }}
        onClick={() => {
          setWithoutTerms(!withoutTerms);
        }}
      >
        <div
          className={
            withoutTerms
              ? "admin__addGoods--radioSquare admin__addGoods--radioChoosen"
              : "admin__addGoods--radioSquare"
          }
        ></div>
        Without terms
      </div>

      <div className="admin__promo--submitWrapper">
        <button
          type="button"
          className="admin__addGoods--submit"
          onClick={submit}
        >
          {promocode ? "Apply Changes" : "Create"}
        </button>
        {promocode && (
          <button
            type="button"
            className="admin__promo--deactivate"
            onClick={deactivate}
          >
            <div className="admin__promo--deactivateSquare"></div>
            {promocode.active ? "Deactivate" : "Activate"}
          </button>
        )}
      </div>
    </div>
  );
};
